import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { ReactElement } from "react";
import { match } from "ts-pattern";
import { Icon } from "../Icon/Icon";
import { SanityImageType } from "../SanityImage";
import { Price } from "../types/price";
import { prefilteredComparableSuites } from "./helpers";
import { ImageValue } from "./ImageValue";
import { SuiteCard, SuiteCardImage } from "./SuiteCard";
import styles from "./SuitesComparison.module.scss";
import { SuiteSelect } from "./SuiteSelect";
import { TableFeatures } from "./TableFeatures";

export type SuiteFeatureValue =
  | SuiteFeatureValueString
  | SuiteFeatureValueBoolean
  | SuiteFeatureValueStringCaveat
  | SuiteFeatureValueBooleanCaveat
  | SuiteFeatureValueImage;

type SuiteFeatureValueString = {
  type: "string";
  value: string;
};

type SuiteFeatureValueBoolean = {
  type: "boolean";
  value: boolean;
};

type SuiteFeatureValueStringCaveat = {
  type: "string-caveat";
  value: string;
  caveat: string;
};

type SuiteFeatureValueBooleanCaveat = {
  type: "boolean-caveat";
  value: boolean;
  caveat: string;
};

type SuiteFeatureValueImage = {
  type: "image";
  value: SanityImageType;
};

export type FeatureTemplate = {
  groups: FeatureGroup[];
};

type FeatureGroup = {
  variant: "big" | "small";
  sectionTitle: string;
  features: FeatureRow[];
};

export type FeatureRow = {
  label: string;
  type: "string" | "boolean";
  key: string;
};

export type ComparableSuite = {
  _key: string;
  suiteCategory: string;
  name: string;
  image: SanityImageType;
  features: Record<string, SuiteFeatureValue>;
  price?: Price;
};

type Props = {
  variant: "compact" | "extended";
  numberOfComparableSuites: number;
  title: string;
  body?: string | ReactElement;
  suites: ComparableSuite[];
  selectedSuiteCategory: string | undefined;
  template: FeatureTemplate;
  renderFooterButton?: (suite: ComparableSuite) => ReactElement;
  priceLabel: string;
  dataContentPiece: string;
};

export const SuitesComparison: React.FC<React.PropsWithChildren<Props>> = ({
  numberOfComparableSuites,
  title,
  body,
  template,
  suites,
  selectedSuiteCategory,
  priceLabel,
  renderFooterButton,
  dataContentPiece,
  variant
}) => {
  const maxNumberOfComparableSuites = Math.min(
    suites.length,
    numberOfComparableSuites
  );

  const filteredComparableSuites = prefilteredComparableSuites(
    selectedSuiteCategory,
    suites,
    maxNumberOfComparableSuites
  );

  const [selectedSuites, setSelectedSuites] = useState<ComparableSuite[]>(
    filteredComparableSuites
  );

  useEffect(() => {
    if (numberOfComparableSuites != selectedSuites.length) {
      setSelectedSuites(filteredComparableSuites);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numberOfComparableSuites]);

  const internalOnSuiteSelectChanged =
    (suiteSelectIndex) => (newSelectedSuite: ComparableSuite) => {
      const newSelection = [...selectedSuites];
      newSelection[suiteSelectIndex] = newSelectedSuite;
      setSelectedSuites(newSelection);
    };

  const columnCount =
    variant == "extended" ? selectedSuites.length + 1 : selectedSuites.length;

  const suiteSelects = selectedSuites.map((suite, index) => {
    return (
      <th
        key={index}
        className={clsx(styles.columnValue, styles.columnHeadValueSize)}
      >
        <div className={styles.suiteSelectWrapper}>
          <SuiteSelect
            className={styles.suiteSelect}
            suites={suites}
            selectedSuite={suite}
            selectedSuites={selectedSuites}
            onChange={internalOnSuiteSelectChanged(index)}
            dataContentPiece={`suite-select-${index}`}
          />
        </div>
      </th>
    );
  });

  return (
    <div
      className={clsx(styles.container, {
        [styles.containerCompact]: variant == "compact",
        [styles.containerExtended]: variant == "extended"
      })}
      data-content-piece={dataContentPiece}
    >
      <table
        className={clsx(styles.table, {
          [styles.tableCompact]: variant == "compact",
          [styles.tableExtended]: variant == "extended"
        })}
      >
        <caption>
          <div className={styles.headerTitle}>{title}</div>
          {body && <div className={styles.headerBody}>{body}</div>}
        </caption>
        <thead className={styles.bodyCards}>
          <tr className={styles.headerRow} data-content-piece="suite-selects">
            {variant == "extended" && (
              <th
                className={clsx(
                  styles.columnHead,
                  styles.columnHeaderHead,
                  styles.columnHeadSize
                )}
              ></th>
            )}
            {suiteSelects}
          </tr>
        </thead>
        <tbody className={styles.bodyCards}>
          <tr className={styles.headerRow}>
            {variant == "extended" && (
              <th
                className={clsx(styles.columnHead, styles.columnHeaderHead)}
              ></th>
            )}
            {selectedSuites.map((s, index) => (
              <td
                key={index}
                className={clsx(styles.columnValue, {
                  [styles.selectedColumnValue]:
                    s.suiteCategory == selectedSuiteCategory
                })}
              >
                <SuiteCardImage
                  image={s.image}
                  name={s.name}
                  isSelected={s.suiteCategory == selectedSuiteCategory}
                />
              </td>
            ))}
          </tr>
          <tr className={clsx(styles.headerRow, styles.rowWithCards)}>
            {variant == "extended" && (
              <th
                className={clsx(styles.columnHead, styles.columnHeaderHead)}
              ></th>
            )}
            {selectedSuites.map((s, index) => (
              <td
                key={index}
                className={clsx(styles.columnValue, {
                  [styles.selectedColumnValue]:
                    s.suiteCategory == selectedSuiteCategory
                })}
              >
                <SuiteCard
                  priceLabel={priceLabel}
                  price={s.price}
                  isSelected={s.suiteCategory == selectedSuiteCategory}
                />
              </td>
            ))}
          </tr>
          <tr className={styles.shadowRow}>
            <td colSpan={columnCount}></td>
          </tr>
        </tbody>

        {template.groups.map((group, index) => {
          return (
            <TableFeatures
              variant={variant}
              size={group.variant}
              caption={group.sectionTitle}
              columnCount={columnCount}
              key={index}
            >
              {group.features.map((feature) => {
                return (
                  <RenderFeature
                    variant={variant}
                    key={feature.key}
                    feature={feature}
                    suites={selectedSuites}
                    selectedSuiteCategory={selectedSuiteCategory}
                    columnCount={columnCount}
                  />
                );
              })}
            </TableFeatures>
          );
        })}
        {renderFooterButton && (
          <tfoot>
            <tr>
              {variant == "extended" && <th></th>}
              {selectedSuites.map((s, index) => (
                <td key={index}>{renderFooterButton(s)}</td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};

type RenderFeatureProps = {
  variant: "compact" | "extended";
  feature: FeatureRow;
  suites: ComparableSuite[];
  selectedSuiteCategory: string | undefined;
  columnCount: number;
};

const RenderFeature: React.FC<React.PropsWithChildren<RenderFeatureProps>> = ({
  variant,
  feature,
  suites,
  selectedSuiteCategory,
  columnCount
}) => {
  const tds = suites.map((s, index) => {
    const value = s.features?.[feature.key];

    return (
      <td
        key={`${index}-${s._key}-${feature.key}`}
        className={clsx(styles.columnValue, {
          [styles.selectedColumnValue]: selectedSuiteCategory == s.suiteCategory
        })}
      >
        <RenderFeatureValue suiteValue={value} />
      </td>
    );
  });

  return variant == "extended" ? (
    <tr className={styles.rowValue}>
      <th scope="row" className={styles.columnHead}>
        {feature.label}
      </th>
      {tds}
    </tr>
  ) : (
    <React.Fragment>
      <tr className={styles.rowCaptionValues}>
        <th colSpan={columnCount} scope="row" className={styles.columnHead}>
          {feature.label}
        </th>
      </tr>
      <tr className={styles.rowValue}>{tds}</tr>
    </React.Fragment>
  );
};

type RenderFeatureValueProps = {
  suiteValue: SuiteFeatureValue;
};
const RenderFeatureValue: React.FC<
  React.PropsWithChildren<RenderFeatureValueProps>
> = ({ suiteValue }) => {
  const value = match(suiteValue)
    .with({ type: "boolean" }, (value) =>
      value.value == true ? (
        <Icon
          name="check"
          padding={false}
          className={styles.iconTrue}
          size="1.5x"
        />
      ) : (
        <Icon
          name="close"
          padding={false}
          className={styles.iconFalse}
          size="1.5x"
        />
      )
    )
    .with({ type: "string" }, ({ value }) => value)
    .with({ type: "string-caveat" }, ({ value, caveat }) => (
      <React.Fragment>
        {value}
        <div className={styles.caveatDisclaimer}>{caveat}</div>
      </React.Fragment>
    ))
    .with({ type: "boolean-caveat" }, ({ caveat }) => caveat)
    .with({ type: "image" }, ({ value }) => <ImageValue image={value} />)
    .otherwise(() => "");

  return <React.Fragment>{value || ""}</React.Fragment>;
};
